import React, { useCallback } from 'react';

import { X, XCircle } from 'lucide-react';

import { Popover, PopoverTrigger, PopoverContent, Badge } from '#/components/ui';

// TODO: WE NEED TO REFINE AND CHANGE THE LOGIC LATER

const humanLabel = key =>
    key
        .split('_')
        .map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))
        .join(' ');

const AppliedFilters = ({
    queryParams,
    handleFilterRemove,
    handleFilterChange,
    onClearFilters,
    moreFiltersConfig = [],
}) => {
    const moreKeys = moreFiltersConfig.map(f => f.name);

    const isActive = value => (Array.isArray(value) ? value.length > 0 : value != null && value !== '');

    const handleRemoveItem = useCallback(
        (key, item) => () => {
            const next = queryParams[key].filter(v => v !== item);
            if (next.length === 0) {
                handleFilterRemove(key);
            } else {
                handleFilterChange(key, next);
            }
        },
        [queryParams, handleFilterRemove, handleFilterChange]
    );

    const handleClearMoreFilters = useCallback(
        e => {
            e.stopPropagation();
            moreKeys.forEach(handleFilterRemove);
        },
        [moreKeys, handleFilterRemove]
    );

    const handleRemove = useCallback(key => () => handleFilterRemove(key), [handleFilterRemove]);

    const renderBadge = (key, val) => {
        if (Array.isArray(val)) {
            if (val.length === 1) {
                return renderSingleBadge(key, val[0]);
            }
            return renderMultiBadge(key, val);
        }
        return renderSingleBadge(key, val);
    };

    const renderSingleBadge = (key, val) => (
        <Badge
            key={key}
            className='bg-lighterBlue font-normal w-fit text-gray500 text-xs flex items-center gap-2 rounded-full px-3 py-1 capitalize'
        >
            {`${humanLabel(key)}: ${val}`}
            <X className='w-4 h-4 cursor-pointer' onClick={handleRemove(key)} />
        </Badge>
    );

    const renderMultiBadge = (key, val) => (
        <Popover key={key}>
            <PopoverTrigger asChild>
                <Badge className='bg-lighterBlue w-fit font-normal text-gray500 text-xs flex items-center gap-1 rounded-full px-3 py-1 cursor-pointer'>
                    {`${humanLabel(key)}: ${val[0]}`}
                    <span className='text-[10px] font-inter bg-primary text-white font-semibold flex justify-center items-center rounded-full w-5 h-5 p-1'>
                        +{val.length - 1}
                    </span>
                    <X className='w-4 h-4 cursor-pointer' onClick={handleRemove(key)} />
                </Badge>
            </PopoverTrigger>
            <PopoverContent className='bg-white w-fit max-h-80 overflow-auto shadow-md rounded-md p-2'>
                <div className='flex flex-col gap-2'>
                    {val.map(item => (
                        <Badge
                            key={item}
                            className='bg-lighterBlue w-fit font-normal text-gray500 text-xs flex items-center gap-2 rounded-full px-3 py-1 capitalize cursor-pointer'
                        >
                            <span className='text-xs font-medium text-gray800'>{item}</span>
                            <X className='w-4 h-4 text-gray-500 cursor-pointer' onClick={handleRemoveItem(key, item)} />
                        </Badge>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );

    const appliedMoreKeys = moreKeys.filter(k => isActive(queryParams[k]));

    const renderMoreFilterBadge = () => (
        <Popover>
            <PopoverTrigger asChild>
                <Badge className='text-textPrimary text-xs font-medium  flex items-center gap-1 bg-blueLight50 rounded-full px-3 py-1 cursor-pointer'>
                    <span className='font-semibold'>{appliedMoreKeys?.length} more filters</span>
                    <X className='flex-shrink-0 w-4 h-4 ml-2 cursor-pointer' onClick={handleClearMoreFilters} />
                </Badge>
            </PopoverTrigger>
            <PopoverContent className='bg-white w-fit shadow-md rounded-md p-3'>
                <div className='flex flex-col gap-3'>
                    {appliedMoreKeys?.map(key => {
                        const val = queryParams[key];

                        if (Array.isArray(val) && val.length > 1) {
                            return renderMultiBadge(key, val);
                        }

                        return renderSingleBadge(key, Array.isArray(val) ? val[0] : val);
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );

    const mainBadges = Object.entries(queryParams)
        .filter(([k, v]) => k !== 'ordering' && !moreKeys.includes(k) && isActive(v))
        .map(([key, val]) => renderBadge(key, val));

    const activeFilterKeys = Object.entries(queryParams).filter(([k, v]) => k !== 'ordering' && isActive(v));

    const renderClearBadge = () =>
        activeFilterKeys.length > 0 ? (
            <Badge
                className='bg-transparent text-gray500 font-dmSans flex items-center gap-1 text-xs font-medium cursor-pointer'
                onClick={onClearFilters}
            >
                <XCircle className='w-4 h-4' /> Clear Filters
            </Badge>
        ) : null;

    return (
        <div className='flex flex-wrap gap-2 px-5 pb-4 mt-4'>
            {mainBadges}
            {appliedMoreKeys.length > 0 && renderMoreFilterBadge()}
            {renderClearBadge()}
        </div>
    );
};

export { AppliedFilters };
